import { useEffect, useEffectEvent, useRef } from 'react';
import { useFullscreenDocument } from '@mantine/hooks';
import { useLocalParticipant, usePersistentUserChoices, useTrackToggle } from '@livekit/components-react';
import { Track } from 'livekit-client';

import { useCallLayout } from './call/call-layout';
import { browserSupportsScreenSharing, SCREEN_SHARE_CAPTURE_OPTIONS } from './call/control-bar';
import type { CallPanel } from './call/top-bar';
import { useLocalHand } from './reactions';
import { i18n } from '@/i18n';
import { isEditableTarget } from '@/shared/is-editable-target';
import { notifyError } from '@/shared/notify';

const SPACE_PRESSES_TARGET =
    'button, a[href], summary, [role="button"], [role="menuitem"], [role="menuitemradio"], [role="checkbox"], [role="radio"], [role="switch"], [role="tab"], [role="option"]';

export function KeyboardShortcuts({
    onTogglePanel,
    onOpenShortcuts,
}: Readonly<{ onTogglePanel: (panel: CallPanel) => void; onOpenShortcuts: () => void }>) {
    const { saveAudioInputEnabled, saveVideoInputEnabled } = usePersistentUserChoices();
    const { localParticipant, isMicrophoneEnabled } = useLocalParticipant();
    const microphone = useTrackToggle({
        source: Track.Source.Microphone,
        onDeviceError: (error) => {
            notifyError(i18n.t('conference:controls.microphone.error'), error);
        },
        onChange: (enabled, isUserInitiated) => {
            if (isUserInitiated) saveAudioInputEnabled(enabled);
        },
    });
    const camera = useTrackToggle({
        source: Track.Source.Camera,
        onDeviceError: (error) => {
            notifyError(i18n.t('conference:controls.camera.error'), error);
        },
        onChange: (enabled, isUserInitiated) => {
            if (isUserInitiated) saveVideoInputEnabled(enabled);
        },
    });
    const screenShare = useTrackToggle({
        source: Track.Source.ScreenShare,
        captureOptions: SCREEN_SHARE_CAPTURE_OPTIONS,
        onDeviceError: (error) => {
            notifyError(i18n.t('conference:controls.screenShare.error'), error);
        },
    });
    const hand = useLocalHand();
    const { toggle: toggleFullscreen } = useFullscreenDocument();
    const { layout, setLayout } = useCallLayout();
    const pushToTalkActiveRef = useRef(false);

    const releasePushToTalk = useEffectEvent(() => {
        if (!pushToTalkActiveRef.current) return;
        pushToTalkActiveRef.current = false;
        void localParticipant.setMicrophoneEnabled(false);
    });

    const modifiedKeyActions: Partial<Record<string, () => void>> = {
        KeyD: () => void microphone.toggle(),
        KeyE: () => void camera.toggle(),
    };

    const singleKeyActions: Partial<Record<string, () => void>> = {
        KeyM: () => void microphone.toggle(),
        KeyV: () => void camera.toggle(),
        KeyD: browserSupportsScreenSharing ? () => void screenShare.toggle() : undefined,
        KeyR: () => void hand.toggle(),
        KeyC: () => {
            onTogglePanel('chat');
        },
        KeyP: () => {
            onTogglePanel('participants');
        },
        KeyW: () => {
            setLayout(layout === 'speaker' ? 'tile' : 'speaker');
        },
        KeyS: () => void toggleFullscreen(),
    };

    const pressPushToTalk = (event: KeyboardEvent) => {
        if (event.target instanceof Element && event.target.closest(SPACE_PRESSES_TARGET)) return;
        event.preventDefault();
        if (event.repeat || isMicrophoneEnabled) return;
        pushToTalkActiveRef.current = true;
        localParticipant.setMicrophoneEnabled(true).catch((error: unknown) => {
            notifyError(i18n.t('conference:controls.microphone.error'), error);
        });
    };

    const handleKeyDown = useEffectEvent((event: KeyboardEvent) => {
        if (event.defaultPrevented || document.querySelector('.mantine-Modal-content')) return;

        if (event.ctrlKey || event.metaKey) {
            const action = event.altKey || event.shiftKey ? undefined : modifiedKeyActions[event.code];
            if (!action) return;
            event.preventDefault();
            if (!event.repeat) action();
            return;
        }

        if (event.altKey || isEditableTarget(event.target)) return;

        if (event.code === 'Space') {
            pressPushToTalk(event);
            return;
        }

        if (event.shiftKey) {
            if (event.key === '?') {
                event.preventDefault();
                onOpenShortcuts();
            }
            return;
        }

        const action = event.repeat ? undefined : singleKeyActions[event.code];
        if (!action) return;
        event.preventDefault();
        action();
    });

    const handleKeyUp = useEffectEvent((event: KeyboardEvent) => {
        if (event.code === 'Space') releasePushToTalk();
    });

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            handleKeyDown(event);
        };
        const onKeyUp = (event: KeyboardEvent) => {
            handleKeyUp(event);
        };
        const onBlur = () => {
            releasePushToTalk();
        };
        globalThis.addEventListener('keydown', onKeyDown);
        globalThis.addEventListener('keyup', onKeyUp);
        globalThis.addEventListener('blur', onBlur);
        return () => {
            globalThis.removeEventListener('keydown', onKeyDown);
            globalThis.removeEventListener('keyup', onKeyUp);
            globalThis.removeEventListener('blur', onBlur);
        };
    }, []);

    return null;
}
